import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../../components/snoop/SummaryDashboard'
import MappingAddForm from '../../components/snoop/MappingAddForm'
import {
  createCatalogRow,
  deleteCatalogRow,
  fetchCatalog,
} from '../../api/operationsClient'
import { PLACEHOLDER_OPS_CATALOG } from '../../utils/snoop/placeholderColumns'

const SECTIONS = ['All', 'Singles', 'Albums', 'Videos', 'Distribution', 'Physical']

/** Product types offered when adding, per section. */
const ADD_PRODUCT_TYPES = {
  All: ['Singles', 'Albums', 'Videos', 'Distribution', 'Distribution Album', 'Physical'],
  Singles: ['Singles'],
  Albums: ['Albums'],
  Videos: ['Videos'],
  Distribution: ['Distribution', 'Distribution Album'],
  Physical: ['Physical'],
}

const SECTION_TAB_BASE =
  'rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300'
const SECTION_TAB_ACTIVE = `${SECTION_TAB_BASE} bg-gray-900 text-white shadow-sm`
const SECTION_TAB_IDLE = `${SECTION_TAB_BASE} text-gray-600 hover:bg-gray-100 hover:text-gray-900`

const btnClass =
  'inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

/**
 * Operations -> Catalog.
 *
 * Section tabs filter by product type. "Show All" switches the API from
 * `mapped` (the default, which hides taken-down products) to `all`, which also
 * merges in unmapped Assets folders as placeholder rows. Taken-down products
 * are greyed via `muteRowKey` rather than shown as a column, matching upstream.
 */
export default function OpsCatalogPage() {
  const [section, setSection] = useState('All')
  const [showAll, setShowAll] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const mapped = showAll ? 'all' : 'mapped'

  const fetchData = useCallback(
    () => fetchCatalog({ mapped, section }),
    // refreshKey forces a refetch after an add/delete.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapped, section, refreshKey],
  )

  const onDeleteRow = useCallback(async (row) => {
    // Placeholder rows are virtual Assets folders with no catalog row to delete.
    if (row?._is_placeholder) {
      throw new Error('This is an unmapped Assets folder, not a catalog row')
    }
    const encoreId = row?.['Encore ID']
    if (encoreId == null || String(encoreId).trim() === '') {
      throw new Error('Missing Encore ID')
    }
    await deleteCatalogRow(encoreId)
  }, [])

  const defaultSort = useMemo(
    () => [{ column: 'Encore ID', direction: 'desc' }],
    [],
  )

  // Server already returns section-appropriate columns; hide the internal keys.
  const hiddenColumns = useMemo(
    () => [
      'Parent Encore ID',
      '_is_placeholder',
      '_box_folder_id',
      '_parent_encore_id',
      '_encore_id',
      '_taken_down',
      '_isrc',
      '_in_isrc_bank',
      '_orphan_child',
      '_tracks',
    ],
    [],
  )

  const addTypes = ADD_PRODUCT_TYPES[section] || ADD_PRODUCT_TYPES.All

  const addForm = showAdd ? (
    <MappingAddForm
      title={`Add ${section === 'All' ? 'catalog product' : section}`}
      submitLabel="Add"
      fields={[
        { name: 'encore_id', label: 'Encore ID', required: true },
        {
          name: 'product_type',
          label: 'Product Type',
          type: 'select',
          options: addTypes.map((t) => ({ value: t, label: t })),
          defaultValue: addTypes[0],
        },
        { name: 'artist', label: 'Artist' },
        { name: 'title', label: 'Title' },
        { name: 'release_date', label: 'Release Date', placeholder: 'YYYY-MM-DD' },
        { name: 'isrc', label: 'ISRC' },
        { name: 'upc', label: 'UPC' },
        { name: 'version', label: 'Version' },
        { name: 'duration', label: 'Duration' },
        { name: 'notes', label: 'Notes' },
        { name: 'og_release_date', label: 'OG Release Date', placeholder: 'YYYY-MM-DD' },
        { name: 'box_folder_id', label: 'Box Folder ID' },
        { name: 'partner', label: 'Partner' },
        { name: 'c_p_line', label: 'C/P Line' },
        { name: 'parent_encore_id', label: 'Parent Encore ID', placeholder: 'for album tracks' },
      ]}
      onSubmit={async (values) => {
        await createCatalogRow(values)
        setRefreshKey((k) => k + 1)
      }}
    />
  ) : null

  // Matches upstream: Add + Show All sit beside the search box; the section
  // tabs are the row above the table.
  const searchActions = (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        className={btnClass}
        onClick={() => setShowAdd((on) => !on)}
        aria-pressed={showAdd}
      >
        {showAdd ? 'Close add' : section === 'All' ? 'Add' : `Add ${section}`}
      </button>
      <label
        className="inline-flex items-center gap-2 text-sm text-gray-700"
        title="Include taken-down products and unmapped Assets folders"
      >
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300"
          checked={showAll}
          onChange={(e) => setShowAll(e.target.checked)}
          aria-label="Show All"
        />
        Show All
      </label>
    </div>
  )

  const extraActions = (
    <div className="flex flex-wrap items-center gap-2">
      <div
        className="inline-flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1"
        role="tablist"
        aria-label="Catalog product type"
      >
        {SECTIONS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={section === name}
            className={section === name ? SECTION_TAB_ACTIVE : SECTION_TAB_IDLE}
            onClick={() => setSection(name)}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <SummaryDashboard
      title={`Catalog · ${section}`}
      subtitle="Encore product registry · Show All for taken-down + unmapped Assets · Postgres SoT"
      fetchData={fetchData}
      defaultSort={defaultSort}
      searchPlaceholder="Search encore id, artist, title, ISRC, folder…"
      searchActions={searchActions}
      onDeleteRow={onDeleteRow}
      extraActions={extraActions}
      addForm={addForm}
      layout="mapping"
      truncateColumns={['Artist', 'Title', 'Version', 'Notes']}
      hiddenColumns={hiddenColumns}
      muteRowKey="_taken_down"
      placeholderColumns={PLACEHOLDER_OPS_CATALOG}
      tableKey={`ops-catalog-${section}-${mapped}-${refreshKey}`}
    />
  )
}
